import { Card, CardContent, Skeleton, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { dashboardPercentChange } from '../../trend';
import { DashboardMetricComparison } from '../DashboardMetricComparison';
import { DashboardMetricSparkline } from '../DashboardMetricSparkline';

interface DashboardMetricCardViewProps {
  label: string;
  value: string;
  current: number | undefined;
  previous: number | undefined;
  comparisonPeriod?: string | null | undefined;
  timelineValues: readonly number[];
  loading: boolean;
  error: boolean;
  footer?: React.ReactNode;
}

export function DashboardMetricCardView({
  label,
  value,
  current,
  previous,
  comparisonPeriod,
  timelineValues,
  loading,
  error,
  footer,
}: DashboardMetricCardViewProps) {
  const hasComparison = dashboardPercentChange(current, previous) !== null;
  const hasTimeline = timelineValues.some(Number.isFinite);

  return (
    <Card
      className={cn(
        'bg-card hover:border-border-strong ease-fluid flex h-full min-w-0 flex-col overflow-hidden rounded-xl transition-colors duration-[var(--duration-fluid)]',
        metricCardHeightClassName(loading, hasComparison, hasTimeline),
      )}
    >
      <CardContent className="@container flex flex-1 flex-col p-3.5">
        <p
          className="text-muted-foreground line-clamp-2 min-w-0 text-[13px] leading-5 font-medium break-words whitespace-normal"
          title={label}
        >
          {label}
        </p>

        <div className="mt-1.5 flex flex-1 flex-col">
          {loading ? <Skeleton className="h-8 w-28" /> : null}
          {error ? (
            <p className="text-destructive text-sm">{dashboardT('dashboard.widget.error')}</p>
          ) : null}
          {!loading && !error ? (
            <>
              <p
                className={cn(
                  'text-[clamp(1rem,13cqi,1.5rem)] leading-tight font-semibold tracking-[-0.02em]',
                  current === undefined && 'text-muted-foreground',
                )}
              >
                {value}
              </p>
              <DashboardMetricComparison
                current={current}
                previous={previous}
                period={comparisonPeriod}
              />
              <DashboardMetricSparkline label={label} values={timelineValues} />
            </>
          ) : (
            <div className="mt-auto h-8" />
          )}
        </div>
      </CardContent>
      {footer}
    </Card>
  );
}

function metricCardHeightClassName(loading: boolean, hasComparison: boolean, hasTimeline: boolean) {
  if (loading || (hasComparison && hasTimeline)) return 'min-h-36';
  if (hasComparison || hasTimeline) return 'min-h-32';
  return 'min-h-24';
}
